import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TabsContent } from '@/components/ui/tabs';

export default function ContractTab({ formData, setFormData, formErrors, t, direction }) {
  const isAr = direction === 'rtl';
  return (
    <TabsContent value="contract" className="space-y-3 mt-3 h-[480px] overflow-y-auto pe-1">
      <div className="bg-white rounded-lg border border-[#C4BFF0] p-3 space-y-2">
        <p className="text-xs font-semibold text-[#655dc6]/70 tracking-wide">{t('contract_info')}</p>
        <div className="space-y-1">
          <Label className="text-xs text-start block">{t('hire_date')}</Label>
          <Input className={formErrors.hire_date ? 'border-red-500' : ''} type="date" value={formData.hire_date} onChange={(e) => setFormData(prev => ({ ...prev, hire_date: e.target.value }))} data-testid="input-hire-date" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('contract_start_date')}</Label>
            <Input className={formErrors.contract_start_date ? 'border-red-500' : ''} type="date" value={formData.contract_start_date} onChange={(e) => setFormData(prev => ({ ...prev, contract_start_date: e.target.value }))} data-testid="input-contract-start" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-start block">{t('contract_end_date')}</Label>
            <Input className={formErrors.contract_end_date ? 'border-red-500' : ''} type="date" value={formData.contract_end_date} onChange={(e) => setFormData(prev => ({ ...prev, contract_end_date: e.target.value }))} data-testid="input-contract-end" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-start block">{t('qiwa_status')}</Label>
          <Select value={formData.qiwa_status} onValueChange={(v) => setFormData(prev => ({ ...prev, qiwa_status: v }))} dir={direction}>
            <SelectTrigger data-testid="input-qiwa-status"><SelectValue placeholder={t('qiwa_status')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t('active_on_qiwa')}</SelectItem>
              <SelectItem value="inactive">{t('not_on_qiwa')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Annual leave policy — per employee (21 or 30 days).
            Hidden for employees who accrue no leaves: agency contracted
            workers (أجير) AND percentage doctors (طبيب نسبة). */}
        {!formData.is_contracted_worker
         && !(formData.is_doctor && formData.doctor_type === 'percentage') && (
        <div className="space-y-1">
          <Label className="text-xs text-start block">
            {isAr ? 'مدة الإجازة السنوية' : 'Annual Leave Policy'}
          </Label>
          <Select
            value={String(formData.annual_leave_days || 30)}
            onValueChange={(v) => setFormData(prev => ({ ...prev, annual_leave_days: parseInt(v, 10) }))}
            dir={direction}
          >
            <SelectTrigger data-testid="input-annual-leave-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="21">{isAr ? '21 يوم (نظام العمل — أقل من 5 سنوات)' : '21 days (Labor Law — under 5 yrs)'}</SelectItem>
              <SelectItem value="30">{isAr ? '30 يوم (نظام العمل — 5 سنوات فأكثر / سياسة سخية)' : '30 days (Labor Law — 5+ yrs / generous policy)'}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-[#655dc6]/40">
            {isAr
              ? 'يُحدّد استحقاق الموظف من الإجازة السنوية. تَنطبق على الـ FIFO والمبلغ النقدي والمخالصات.'
              : 'Determines employee\'s annual leave entitlement. Applies to FIFO, cash value, and clearance.'}
          </p>
        </div>
        )}

        {/* Healthcare Practitioner toggle */}
        <div className="mt-2 flex items-center justify-between gap-3 p-2 rounded-md bg-[#E8F7FB] border border-[#B0DDE8]">
          <div className="flex-1 min-w-0">
            <Label htmlFor="is_practitioner_switch" className="text-xs font-semibold text-[#1F7A90] cursor-pointer">
              {isAr ? 'ممارس صحي' : 'Healthcare Practitioner'}
            </Label>
            <p className="text-[10px] text-[#1F7A90]/70 mt-0.5">
              {isAr
                ? 'عند التفعيل ينتقل الموظف تلقائياً إلى قسم العلاقات الحكومية لإدارة رخصه (SCFHS، BLS، إلخ).'
                : 'When enabled, the employee is automatically transferred to Government Affairs to manage their licenses (SCFHS, BLS, etc).'}
            </p>
          </div>
          <Switch
            id="is_practitioner_switch"
            checked={!!formData.is_practitioner}
            onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_practitioner: v }))}
            data-testid="input-is-practitioner"
          />
        </div>

        {/* Employment type — controls attendance / Saudization treatment */}
        <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 space-y-1">
          <Label className="text-xs font-semibold text-amber-800">
            {isAr ? 'طبيعة التعاقد' : 'Employment Type'}
          </Label>
          <p className="text-[10px] text-amber-700/80">
            {isAr
              ? 'الأنواع غير "حضور منتظم" تُستثنى من احتساب الغياب اليومي ولا تَظهر في تقارير الحضور.'
              : 'Types other than "On-site" are excluded from daily attendance metrics.'}
          </p>
          <Select
            value={formData.employment_type || 'on_site'}
            onValueChange={(v) => setFormData(prev => ({ ...prev, employment_type: v }))}
            dir={direction}
          >
            <SelectTrigger data-testid="input-employment-type" className="bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="on_site">{isAr ? 'حضور منتظم' : 'On-site (regular)'}</SelectItem>
              <SelectItem value="remote">{isAr ? 'عن بُعد' : 'Remote'}</SelectItem>
              <SelectItem value="flexible">{isAr ? 'دوام مرن' : 'Flexible'}</SelectItem>
              <SelectItem value="consultant">{isAr ? 'مستشار / متعاون' : 'Consultant'}</SelectItem>
              <SelectItem value="attendance_based">{isAr ? 'على الحضور فقط (يومي)' : 'Attendance-only (per-day)'}</SelectItem>
              <SelectItem value="part_time" data-testid="emp-type-part-time">{isAr ? 'دوام جزئي' : 'Part-time'}</SelectItem>
            </SelectContent>
          </Select>
          {formData.employment_type && formData.employment_type !== 'on_site' && (
            <Input
              placeholder={isAr ? 'سبب التصنيف (للتوثيق)' : 'Reason / justification (for audit)'}
              value={formData.employment_type_reason || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, employment_type_reason: e.target.value }))}
              className="bg-white text-xs"
              data-testid="input-employment-reason"
            />
          )}
        </div>
      </div>
    </TabsContent>
  );
}
